import {
  CareOrderStatusEnum,
  cleanupInitialFilterDateRange,
  FilterClauseType,
  filterExistsAndExcludes,
  getTypedKeys,
  IconName,
  isExcludedByCurrencyOrBasePairFilters,
  removeEmptyFilters,
  useBasePairFilter,
  useCurrencyOfSymbolFilter,
  useSecuritiesContext,
  useSidesFilter,
  useSymbolsFilter,
  type CptySettlementTypeEnum,
  type DateRangeFilter,
  type FilterableProperty,
  type FilterClause,
  type Leaves,
  type Security,
  type UseFilterBuilderProps,
} from '@talos/kyoko';
import { compact, isArray, isEqual, startCase, values } from 'lodash-es';
import { startTransition, useCallback, useMemo, useState, type SetStateAction } from 'react';
import { passesCareOrderDateRangeFilter } from './careOrderDateRangeFilter';
import type { CareOrderTimestampField } from './careOrderTimestampFields';
import type { CareOrderBlotterEntity, CareOrderRow } from './types';
import { useCptySettlementTypeFilter } from './useCptySettlementTypeFilter';

export { careOrderTimestampFields } from './careOrderTimestampFields';
export type { CareOrderTimestampField } from './careOrderTimestampFields';

export interface CareOrderFilter extends DateRangeFilter<CareOrderTimestampField> {
  Statuses?: string[];
  Symbols?: string[];
  Groups?: string[];
  Sides?: string;
  OriginationTrader?: string;
  CptySettlementTypes?: CptySettlementTypeEnum[];
  Currencies?: string[];
  BasePairs?: string[];
}

interface UseCareOrderFilterParams {
  initialFilter?: CareOrderFilter;
  saveFilter: (newFilter: any) => void;
  includeOpenCareOrdersOutsideDateRange?: boolean;
}

export function useCareOrderFilter({
  initialFilter,
  saveFilter,
  includeOpenCareOrdersOutsideDateRange = true,
}: UseCareOrderFilterParams) {
  const [filter, setFilter] = useState(() => cleanupInitialFilterDateRange(initialFilter));
  const { securitiesBySymbol } = useSecuritiesContext();

  const changeFilter = useCallback(
    (action: SetStateAction<CareOrderFilter>) => {
      startTransition(() => {
        const priorFilter = filter;
        const newFilter = action instanceof Function ? action(filter) : action;
        if (!isEqual(priorFilter, newFilter)) {
          setFilter(newFilter);
          saveFilter(newFilter);
        }
      });
    },
    [filter, saveFilter]
  );

  const careOrderBranchFilter = useCallback(
    (data: CareOrderRow) =>
      careOrderPassesFilter(data, filter, includeOpenCareOrdersOutsideDateRange, securitiesBySymbol),
    [filter, includeOpenCareOrdersOutsideDateRange, securitiesBySymbol]
  );

  const sidesFilter = useSidesFilter();
  const symbolsFilter = useSymbolsFilter();
  const currencyOfSymbolFilter = useCurrencyOfSymbolFilter();
  const basePairFilter = useBasePairFilter();
  const cptySettlementTypeFilter = useCptySettlementTypeFilter();

  const filterableProperties: FilterableProperty[] = useMemo(
    () => [
      sidesFilter,

      {
        key: 'Statuses',
        label: 'Status',
        icon: IconName.CheckCircle,
        options: values(CareOrderStatusEnum),
        getOptionLabel: startCase,
      },
      {
        key: 'OriginationTrader',
        label: 'Origination Trader',
        icon: IconName.User,
        control: 'text',
      },
      cptySettlementTypeFilter,
      symbolsFilter,
      currencyOfSymbolFilter,
      basePairFilter,
    ],
    [cptySettlementTypeFilter, sidesFilter, symbolsFilter, currencyOfSymbolFilter, basePairFilter]
  );

  const initialFilterClauses = useMemo(() => {
    const clauses: FilterClause[] = [];
    if (filter) {
      getTypedKeys(filter).forEach(key => {
        clauses.push({
          key: key,
          type: FilterClauseType.INCLUSIVE,
          selections: (isArray(filter[key]) ? filter[key] : compact([filter[key]])) as string[],
        });
      });
    }

    return clauses;
  }, [filter]);

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter: CareOrderFilter = removeEmptyFilters<CareOrderFilter>({
          ...curr,
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => [key, filterClausesByPropertyKey.get(key)?.selections])
          ) satisfies CareOrderFilter),
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter]
  );

  const filterBuilderProps = useMemo(
    () =>
      ({
        initialFilterClauses,
        properties: filterableProperties,
        onFilterClausesChanged: handleFilterClausesChanged,
      }) satisfies UseFilterBuilderProps,
    [initialFilterClauses, filterableProperties, handleFilterClausesChanged]
  );

  return {
    filter,
    filterableProperties,
    initialFilterClauses,
    changeFilter,
    careOrderBranchFilter,
    filterBuilderProps,
  };
}

function careOrderPassesFilter(
  data: CareOrderRow,
  filter: CareOrderFilter,
  includeOpenCareOrdersOutsideDateRange: boolean,
  securitiesBySymbol: Map<string, Pick<Security, 'BaseCurrency' | 'QuoteCurrency'>>
) {
  if (!passesCareOrderDateRangeFilter(data, filter, includeOpenCareOrdersOutsideDateRange)) {
    return false;
  }
  if (filterExistsAndExcludes(filter, 'Symbols', data, 'Symbol')) {
    return false;
  }
  if (isExcludedByCurrencyOrBasePairFilters(data?.Symbol, securitiesBySymbol, filter.Currencies, filter.BasePairs)) {
    return false;
  }
  if (filterExistsAndExcludes(filter, 'Statuses', data, 'status')) {
    return false;
  }
  if (filterExistsAndExcludes(filter, 'Sides', data, 'Side')) {
    return false;
  }
  if (filterExistsAndExcludes(filter, 'Groups', data, 'Group')) {
    return false;
  }
  if (filterExistsAndExcludes(filter, 'OriginationTrader', data, 'originationTrader')) {
    return false;
  }
  if (filterExistsAndExcludes(filter, 'CptySettlementTypes', data, 'CptySettlementType')) {
    return false;
  }

  return true;
}

const colIDToFilterBuilderKeyMap = {
  status: 'Statuses',
  Symbol: 'Symbols',
  Group: 'Groups',
  Side: 'Sides',
  CptySettlementType: 'CptySettlementTypes',
} satisfies Partial<Record<Leaves<CareOrderBlotterEntity>, keyof CareOrderFilter>>;

export function colIDToFilterBuilderKey(id: string): keyof CareOrderFilter | undefined {
  return colIDToFilterBuilderKeyMap[id as keyof typeof colIDToFilterBuilderKeyMap];
}
